/*
 * voice-underlay.js — «глушить только голос»: что звучит ПОД заглушкой.
 *
 * Обычная заглушка глушит ВСЁ, что звучит в момент слова: вместе с матом
 * пропадают игра, музыка, подложка. Режим «только голос» кладёт под заглушку
 * то, что разделитель считает НЕ голосом, — и это делается не пересборкой всей
 * дорожки, а ПОДЛОЖКОЙ: вторым входом FFmpeg длиной во всю запись, который
 * почти целиком состоит из нулей и несёт короткие заплатки ровно там, где
 * заглушки.
 *
 * Почему так, а не «сведём дорожку в памяти» (так лейн жил до 24.09.2026):
 * 37-минутный ролик — это 850 МБ PCM на одну копию, а старый лейн держал три
 * (декод, копия-результат, WAV) и не был доступен для видео вовсе. Подложка
 * стоит ПАМЯТИ ровно на заплатки: нули — это ссылки на ОДИН мегабайтный Blob
 * (браузер хранит части Blob ссылками, а не копиями), и FFmpeg читает файл с
 * диска кусками через WORKERFS, как читает сам ролик.
 *
 * Подложка — точное ДОПОЛНЕНИЕ графа глушения ядра (buildGateGraph):
 *     оригинал × (1 − g) + подложка,   подложка = g × «не голос»,
 * где g = floor × min(Σ трапеций, 1) — то же выражение, что у `volume`,
 * посчитанное на тех же кадрах по 128 сэмплов (`asetnsamples=128`,
 * `eval=frame`). Поэтому на краях заглушки звук не проваливается и не
 * удваивается: это кроссфейд между оригиналом и «не голосом», а не два
 * независимых фейда.
 *
 * ⚠️ Совпадение по времени держит граф ядра: `amix` складывает входы ПО НОМЕРУ
 * СЭМПЛА, а не по времени (замер 24.09 на FFmpeg 7.0: у файла, чья звуковая
 * дорожка начинается на 0.5 с позже видео, заплатка без выравнивания ложится
 * на 0.5 с позже). Ядро выравнивает оригинал тем же `aresample=async=1:
 * first_pts=0`, что и извлечение звука для распознавания, — тогда сэмпл i
 * подложки и сэмпл i оригинала — это одно и то же мгновение, и то же
 * мгновение, в котором распознавание нашло слово.
 */
(function (w) {
    'use strict';

    // ⚠️ Обязаны совпадать с графом ядра: GATE_FADE и asetnsamples=128.
    // Сторож — tests/js/profanity-voice-underlay.test.mjs (читает оба файла).
    var FADE = 0.03;
    var FRAME = 128;
    var ZERO_CHUNK = 1 << 20;

    // Граф печатает границы через toFixed(4) — зеркало обязано считать от тех
    // же чисел, иначе край разойдётся на доли миллисекунды там, где их слышно.
    function r4(x) { return parseFloat((+x).toFixed(4)); }

    /* Σ трапеций заглушек, обрезанная до 1: то самое выражение `ramp` ядра. */
    function rampAt(t, regions, fade) {
        var f = typeof fade === 'number' ? fade : FADE;
        var s = 0;
        for (var k = 0; k < regions.length; k++) {
            var r = regions[k];
            var up = (t - r4(Math.max(0, r.start - f))) / f;
            var down = (r4(r.end + f) - t) / f;
            s += Math.min(Math.max(up, 0), 1) * Math.min(Math.max(down, 0), 1);
        }
        return Math.min(s, 1);
    }

    /* Доля подложки в сэмпле i: floor × ramp на НАЧАЛЕ его кадра из 128. */
    function gainAt(i, sr, regions, floor, fade) {
        var n = Math.floor(i / FRAME);
        return floor * rampAt(n * FRAME * (1 / sr), regions, fade);
    }

    /* Отрезки [i0, i1) в сэмплах, где подложка не ноль: заглушка ± фейд,
       с запасом в кадр по обе стороны (g меняется только на границе кадра),
       пересекающиеся — слиты. */
    function spansFor(regions, sr, totalSamples, fade) {
        var f = typeof fade === 'number' ? fade : FADE;
        var rs = (regions || []).filter(function (r) { return r && r.end > r.start; })
            .slice().sort(function (a, b) { return a.start - b.start; });
        var out = [];
        for (var k = 0; k < rs.length; k++) {
            var i0 = Math.max(0, (Math.floor(Math.max(0, rs[k].start - f) * sr / FRAME) - 1) * FRAME);
            var i1 = Math.min(totalSamples, (Math.ceil((rs[k].end + f) * sr / FRAME) + 1) * FRAME);
            if (i1 <= i0) continue;
            var last = out[out.length - 1];
            if (last && i0 <= last.i1) { last.i1 = Math.max(last.i1, i1); last.regions.push(rs[k]); }
            else out.push({ i0: i0, i1: i1, regions: [rs[k]] });
        }
        return out;
    }

    /* Заплатка одного отрезка: g × «не голос», перемежённые каналы, int16.
       nonVoice — массивы каналов, nonVoiceStart — сэмпл записи, с которого они
       начинаются (окно разделителя шире отрезка). Вне окна — ноль: лучше
       тишина, чем чужие сэмплы. */
    function patchFor(span, nonVoice, nonVoiceStart, sr, floor, fade) {
        var ch = nonVoice.length, n = span.i1 - span.i0;
        var out = new Int16Array(n * ch);
        for (var j = 0; j < n; j++) {
            var i = span.i0 + j;
            var g = gainAt(i, sr, span.regions, floor, fade);
            if (g <= 0) continue;
            var li = i - nonVoiceStart;
            for (var c = 0; c < ch; c++) {
                var src = nonVoice[c];
                var x = (li >= 0 && li < src.length) ? src[li] * g : 0;
                x = x > 1 ? 1 : x < -1 ? -1 : x;
                out[j * ch + c] = Math.round(x * 32767);
            }
        }
        return out;
    }

    function wavHeader(sr, ch, dataBytes) {
        var ab = new ArrayBuffer(44), v = new DataView(ab), o = 0;
        function s(str) { for (var i = 0; i < str.length; i++) v.setUint8(o++, str.charCodeAt(i)); }
        function u32(x) { v.setUint32(o, x, true); o += 4; }
        function u16(x) { v.setUint16(o, x, true); o += 2; }
        s('RIFF'); u32(36 + dataBytes); s('WAVE'); s('fmt '); u32(16); u16(1); u16(ch);
        u32(sr); u32(sr * ch * 2); u16(ch * 2); u16(16); s('data'); u32(dataBytes);
        return ab;
    }

    /* Вся подложка: заголовок + нули-ссылки + заплатки на своих местах.
       patches — [{ i0, data: Int16Array (перемежённые каналы) }], по возрастанию
       и без пересечений (так их отдаёт spansFor). Память — только заплатки. */
    function buildWav(sr, ch, totalSamples, patches) {
        var frameBytes = ch * 2;
        var dataBytes = totalSamples * frameBytes;
        if (dataBytes > 0xFFFFFFFF - 36) throw new Error('UNDERLAY_TOO_LONG');
        var zero = new Blob([new ArrayBuffer(ZERO_CHUNK)]);
        var parts = [wavHeader(sr, ch, dataBytes)];
        function zeros(bytes) {
            while (bytes > 0) {
                var n = Math.min(bytes, ZERO_CHUNK);
                parts.push(zero.slice(0, n));
                bytes -= n;
            }
        }
        var cursor = 0;
        for (var k = 0; k < patches.length; k++) {
            var p = patches[k];
            var len = p.data.length / ch;
            if (p.i0 < cursor || p.i0 + len > totalSamples) throw new Error('UNDERLAY_PATCH_OUT_OF_ORDER');
            zeros((p.i0 - cursor) * frameBytes);
            parts.push(p.data);
            cursor = p.i0 + len;
        }
        zeros((totalSamples - cursor) * frameBytes);
        return new Blob(parts, { type: 'audio/wav' });
    }

    /* Сырой f32le от FFmpeg (перемежённые каналы) → массивы каналов. */
    function deinterleave(f32, ch) {
        var n = Math.floor(f32.length / ch), out = [];
        for (var c = 0; c < ch; c++) out.push(new Float32Array(n));
        for (var i = 0; i < n; i++) for (var c2 = 0; c2 < ch; c2++) out[c2][i] = f32[i * ch + c2];
        return out;
    }

    w.ProfanityVoiceUnderlay = {
        FADE: FADE, FRAME: FRAME,
        rampAt: rampAt, gainAt: gainAt, spansFor: spansFor, patchFor: patchFor,
        buildWav: buildWav, deinterleave: deinterleave
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = w.ProfanityVoiceUnderlay;
})(typeof window !== 'undefined' ? window : globalThis);
